
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface ExchangeRate {
  id: string;
  base_currency_id: string;
  target_currency_id: string;
  rate: number;
  created_at: string;
  updated_at: string;
  base_currency: {
    id: string;
    name: string;
    code: string;
    symbol: string;
    created_at: string;
    updated_at: string;
    user_id: string;
  };
  target_currency: {
    id: string;
    name: string;
    code: string;
    symbol: string;
    created_at: string;
    updated_at: string;
    user_id: string;
  };
}

export function useExchangeRates() {
  const queryClient = useQueryClient();
  
  const useExchangeRatesQuery = () => {
    return useQuery({
      queryKey: ['exchange-rates'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('exchange_rates')
          .select('*, base_currency:currencies!base_currency_id(*), target_currency:currencies!target_currency_id(*)');

        if (error) {
          toast({
            title: 'Error loading exchange rates',
            description: error.message,
            variant: 'destructive'
          });
          throw error;
        }

        return data as ExchangeRate[];
      }
    });
  };

  const useUpdateExchangeRateMutation = () => {
    return useMutation({
      mutationFn: async (exchangeRate: { id: string, rate: number }) => {
        const { data, error } = await supabase
          .from('exchange_rates')
          .update({ rate: exchangeRate.rate })
          .eq('id', exchangeRate.id)
          .select('*');

        if (error) {
          toast({
            title: 'Error updating exchange rate',
            description: error.message,
            variant: 'destructive'
          });
          throw error;
        }

        return data?.[0];
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
        toast({
          title: 'Exchange rate updated',
          description: 'The exchange rate has been successfully updated',
        });
      }
    });
  };

  // Function to convert currency amounts
  const convertCurrency = (amount: number, fromCurrencyId: string, toCurrencyId: string) => {
    // If currencies are the same, no conversion needed
    if (fromCurrencyId === toCurrencyId) {
      return amount;
    }

    // For simplicity, we're using a fixed exchange rate of 1:1
    // In a real application, you would retrieve the actual exchange rate from your database
    return amount;
  };

  const { data: exchangeRates = [] } = useExchangeRatesQuery();

  return {
    useExchangeRatesQuery,
    useUpdateExchangeRateMutation,
    convertCurrency,
    exchangeRates
  };
}
